import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
const STORAGE_KEY = 'is-cookie-notice-v1';
/** Waits for the hero intro to settle before appearing (ms) */
const SHOW_DELAY = 1800;
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * First-visit cookie notice with Accept / Reject. The choice is remembered, so it shows only once.
 * Sits bottom-left on desktop, clear of Pixel's corner on the right; full width along the bottom on phones.
 */
export function CookieNotice() {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let seen = false;
    try {
      seen = window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      seen = false;
    }
    if (seen) return;
    const id = window.setTimeout(() => setOpen(true), SHOW_DELAY);
    return () => window.clearTimeout(id);
  }, []);
  const dismiss = (choice: 'accepted' | 'rejected') => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
      window.localStorage.setItem(`${STORAGE_KEY}-choice`, choice);
    } catch {
      /* storage blocked: the notice simply returns next visit */
    }
    setOpen(false);
  };
  return <AnimatePresence data-id="element-47" data-mp="3n27yl8ppigl7">
      {open && <motion.aside role="region" aria-label="Cookie notice" initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 16,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 12,
      scale: 0.98
    }} transition={{
      duration: 0.25,
      ease: EASE
    }} className="fixed inset-x-3 bottom-3 z-[1000] rounded-2xl bg-black p-5 text-white shadow-[0_12px_40px_rgba(0,0,0,0.18)] md:inset-x-auto md:bottom-[calc(1.6*var(--vw))] md:left-[calc(1.6*var(--vw))] md:w-[24rem] md:p-6" data-id="element-48" data-mp="3rzhm2itjci6e">
          <p className="text-[0.95rem] font-semibold leading-snug tracking-[-0.02em]" data-id="element-49" data-mp="oi97q8y0o0gf">We use cookies</p>
          <p className="mt-2 text-sm font-medium leading-snug text-white/60" data-id="element-50" data-mp="2kl4gyn6bpdvb">
            We use cookies to give you the best experience on our website.{' '}
            <Link to="/legal#cookies" className="rounded-sm text-white underline decoration-white/40 underline-offset-2 transition-colors duration-200 ease-out hover:decoration-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-51" data-mp="2fonip05etj4i">
              Learn more
            </Link>
          </p>
          <div className="mt-4 flex gap-2" data-id="element-52" data-mp="1wxwcg7gpy8uc">
            <button type="button" onClick={() => dismiss('accepted')} className="inline-flex h-10 items-center rounded-full bg-white px-6 text-sm font-semibold tracking-[-0.02em] text-black transition-opacity duration-150 ease-out hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-53" data-mp="2an7ytbeyxinp">
              Accept
            </button>
            <button type="button" onClick={() => dismiss('rejected')} className="inline-flex h-10 items-center rounded-full border-2 border-white/40 px-6 text-sm font-semibold tracking-[-0.02em] text-white transition-colors duration-150 ease-out hover:border-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-54" data-mp="140c1doadi6tt">
              Reject
            </button>
          </div>
        </motion.aside>}
    </AnimatePresence>;
}